/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { BoxBlue, Weight } from '@ahamove/icons/order';
import type { BillableWeightResponse } from '@ahm/api-wrappers-core';
import { formatNumber } from '@ahm/common-helpers';
import { FloatingLabelNumberInput } from '@ahm/react-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  Divider,
  Group,
  rem,
  ScrollArea,
  SimpleGrid,
  Space,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { ContextModalProps } from '@mantine/modals';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { openCustomConfirmModal } from '..';
import type { BillableWeightRequestBody } from '@/api/services/billable-weight-fn';
import InfoTooltip from '@/components/info-tooltip';
import { UploadPhoto } from '@/components/upload-photo';
import { useBillableWeight } from '@/features/booking/api/service/use-billable-weight';
import { useTruckService } from '@/features/booking/components/services/use-truck-service';
import { useDetectBanHour } from '@/hooks';
import { getTotalLengthWidthHeight } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface TruckPackageForm {
  weight?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  quantity: number;
  image_url: string[] | string;
}

export type TruckPackageDetailsFormProps = Pick<
  ContextModalProps<{
    initialData?: TruckPackageForm[];
    group?: string;
  }>,
  'innerProps'
> & {
  onClose?: () => void;
};

// export const DEFAULT_TRUCK_PACKAGE_DETAIL_ITEM = {
//   weight: null,
//   length: null,
//   width: null,
//   height: null,
//   quantity: 1,
//   image_url: [],
// };

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function TruckPackageDetailsForm({
  onClose,
  innerProps: { initialData, group },
}: TruckPackageDetailsFormProps) {
  const tCommon = useTranslations('common');
  const tActions = useTranslations('common.actions');
  const tPackageDetails = useTranslations('booking.package_details');
  const {
    billableWeightData,
    selectedService,
    setPackageDetail,
    setTruckSelectedGroup,
    setBillableWeightData,
    setSelectedService,
  } = useBookingStore((state) => state);
  const serviceValidate = useMemo(
    () => selectedService?.vehicle_info,
    [selectedService?.vehicle_info]
  );

  const { truckGroupedServices } = useTruckService();
  const [estBillableWeightData, setEstBillableWeightData] =
    useState<BillableWeightResponse | null>(billableWeightData);
  const prevEstimateRequestBodyRef = useRef<string | null>(null);

  const validationSchema = z.object({
    package_detail: z.array(
      z.object({
        weight: z
          .number({
            required_error: tPackageDetails('errors.weight.require'),
            invalid_type_error: tPackageDetails('errors.weight.require'),
          })
          .min(
            0.001,
            tPackageDetails('errors.weight.limit', {
              min: 0.001,
              max: serviceValidate?.weight,
            })
          ),
        // .max(
        //   serviceValidate?.weight,
        //   tPackageDetails('errors.weight.limit', {
        //     min: 0.001,
        //     max: serviceValidate?.weight,
        //   })
        // ),
        height: z
          .number({
            required_error: tPackageDetails('errors.height'),
            invalid_type_error: tPackageDetails('errors.height'),
          })
          // .max(
          //   serviceValidate?.height_in_cm,
          //   tPackageDetails('errors.package_size.max', {
          //     max: serviceValidate?.height_in_cm,
          //   })
          // )
          .min(1, tPackageDetails('errors.package_size.min')),
        width: z
          .number({
            required_error: tPackageDetails('errors.width'),
            invalid_type_error: tPackageDetails('errors.width'),
          })
          // .max(
          //   serviceValidate?.width_in_cm,
          //   tPackageDetails('errors.package_size.max', {
          //     max: serviceValidate?.width_in_cm,
          //   })
          // )
          .min(1, tPackageDetails('errors.package_size.min')),
        length: z
          .number({
            required_error: tPackageDetails('errors.length'),
            invalid_type_error: tPackageDetails('errors.length'),
          })
          // .max(
          //   serviceValidate?.length_in_cm,
          //   tPackageDetails('errors.package_size.max', {
          //     max: serviceValidate?.length_in_cm,
          //   })
          // )
          .min(1, tPackageDetails('errors.package_size.min')),
        image_url: z.array(z.string()).optional(),
      })
    ),
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    // reset,
    // setValue,
    // getValues,
  } = useForm<{ package_detail: TruckPackageForm[] }>({
    resolver: zodResolver(validationSchema),
    defaultValues: { package_detail: initialData?.package_detail },
    mode: 'onChange',
  });

  const packageDetails =
    useWatch({
      control,
    })?.package_detail ?? [];

  const [debouncedPackageDetails] = useDebouncedValue(packageDetails, 500);

  const invalidPackageDetails = useMemo(() => {
    return Boolean(
      debouncedPackageDetails.some(
        (item) => !item.weight || !item.height || !item.width || !item.length
      )
    );
  }, [debouncedPackageDetails, isValid]);

  const totalLengthWidthHeight = useMemo(
    () => getTotalLengthWidthHeight(debouncedPackageDetails),
    [debouncedPackageDetails, getTotalLengthWidthHeight]
  );

  const packageEstimateBody = useMemo(() => {
    if (
      invalidPackageDetails ||
      !isValid ||
      // paths.length < 2 ||
      !selectedService?._id
    )
      return;

    return {
      package_dimension_unit: 'cm',
      service_id: selectedService._id,
      package_detail: debouncedPackageDetails,
    } as BillableWeightRequestBody;
  }, [
    // paths,
    selectedService?._id,
    debouncedPackageDetails,
    invalidPackageDetails,
  ]);

  const [isEstimating, setIsEstimating] = useState<boolean>(false);
  const [estimateError, setEstimateError] = useState<boolean>(false);

  const { mutate: onBillableWeight } = useBillableWeight({
    onSuccess: (res) => {
      setEstimateError(false);
      setIsEstimating(false);
      setEstBillableWeightData(res);
      // setBillableWeightData(res);
    },
    onError: (error) => {
      setEstimateError(true);
      setIsEstimating(false);
      setEstBillableWeightData(null);
      setBillableWeightData(null);

      const errorData = error?.response?.data;

      if (errorData) {
        openCustomConfirmModal({
          title: tCommon('something_went_wrong'),
          children:
            errorData.description ||
            errorData.title ||
            tCommon('something_went_wrong'),
          confirmLabel: tActions('ok_got_it'),
        });
      }
    },
  });

  useEffect(() => {
    const currentDependencies = JSON.stringify(packageEstimateBody);

    if (
      prevEstimateRequestBodyRef.current !== currentDependencies &&
      packageEstimateBody
    ) {
      setIsEstimating(true);
      onBillableWeight(packageEstimateBody);
    }
    prevEstimateRequestBodyRef.current = currentDependencies;
  }, [onBillableWeight, packageEstimateBody]);

  // const [scrollPosition, onScrollPositionChange] = useState({ x: 0, y: 0 });
  const viewportRef = useRef<HTMLDivElement>(null);
  const { checkServiceInBanFn } = useDetectBanHour();

  const onSubmit = (data: { package_detail: TruckPackageForm[] }) => {
    const { package_detail } = data;
    if (group) {
      setTruckSelectedGroup(group);

      const services = truckGroupedServices?.[group]?.services || [];
      if (services.length > 0) {
        // Tìm dịch vụ có sẵn đầu tiên không bị hạn chế
        const availableTruckService = services.find(
          (service) => !checkServiceInBanFn(service)
        );

        if (availableTruckService) {
          setSelectedService(availableTruckService);
        }
      }
    }
    setPackageDetail(package_detail as TruckPackageForm[]);
    if (estBillableWeightData) {
      setBillableWeightData(estBillableWeightData);
    }
    onClose && onClose();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Divider />
      <Card h="auto" p={0} my={12} radius="md">
        <ScrollArea.Autosize scrollbars="y" viewportRef={viewportRef} mb={12}>
          <Stack gap="sm" w="100%" p="md">
            <Text fz={14}>Kích thước kiện hàng</Text>
            <SimpleGrid cols={3} spacing={rem(8)}>
              <Controller
                control={control}
                name="package_detail.0.length"
                render={({ field: { onChange, value } }) => (
                  <FloatingLabelNumberInput
                    hideControls
                    error={errors.package_detail?.[0]?.length?.message}
                    step={1}
                    value={value}
                    onChange={(v) => onChange(parseFloat(isNaN(v) ? 0 : v))}
                    label={tPackageDetails('length_cm')}
                  />
                )}
              />

              <Controller
                control={control}
                name="package_detail.0.width"
                render={({ field: { onChange, value } }) => (
                  <FloatingLabelNumberInput
                    hideControls
                    step={1}
                    error={errors.package_detail?.[0]?.width?.message}
                    value={value}
                    onChange={(v) => onChange(parseFloat(isNaN(v) ? 0 : v))}
                    label={tPackageDetails('width_cm')}
                  />
                )}
              />

              <Controller
                control={control}
                name="package_detail.0.height"
                render={({ field: { onChange, value } }) => (
                  <FloatingLabelNumberInput
                    hideControls
                    step={1}
                    error={errors.package_detail?.[0]?.height?.message}
                    value={value}
                    onChange={(v) => onChange(parseFloat(isNaN(v) ? 0 : v))}
                    label={tPackageDetails('height_cm')}
                  />
                )}
              />
            </SimpleGrid>
            <Controller
              control={control}
              name="package_detail.0.weight"
              render={({ field: { onChange, value } }) => (
                <FloatingLabelNumberInput
                  hideControls
                  error={errors.package_detail?.[0]?.weight?.message}
                  value={value}
                  onChange={(v) => onChange(parseFloat(isNaN(v) ? 0 : v))}
                  label={tPackageDetails('weight_kg')}
                />
              )}
            />

            <Group gap={rem(6)} align="center" wrap="nowrap">
              <Text size="sm" c="neutral.7" fw={500}>
                {tPackageDetails('volumetric_weight')}{' '}
                <InfoTooltip
                  label={tPackageDetails('volumetric_weight')}
                  onClick={() => {
                    openCustomConfirmModal({
                      title: tPackageDetails('what_is_volumetric_weight'),
                      children: tPackageDetails.rich('volumetric_weight_is', {
                        dim_factor: formatNumber(
                          estBillableWeightData?.dim_factor ?? 5000
                        ),
                      }),
                      confirmLabel: tActions('got_it'),
                    });
                  }}
                />
              </Text>
              <Space flex={1} />
              <Text
                size="sm"
                c="neutral.7"
                fw={500}
                styles={{ root: { whiteSpace: 'nowrap' } }}
              >
                {formatNumber(
                  estBillableWeightData?.package_weight_details?.[0]
                    ?.volumetric_weight ?? 0
                )}{' '}
                (Kg)
              </Text>
            </Group>

            <Group gap={rem(6)} align="center" wrap="nowrap" pos="relative">
              <Divider
                display={{ base: 'none', md: 'block' }}
                orientation="vertical"
                pos="absolute"
                top={0}
                bottom={0}
                left={rem(-8)}
              />
              <Text size="sm" c="secondary.5" fw={700}>
                {tPackageDetails('chargeable_weight')}{' '}
                <InfoTooltip
                  label={tPackageDetails('chargeable_weight')}
                  onClick={() => {
                    openCustomConfirmModal({
                      title: tPackageDetails('what_is_chargeable_weight'),
                      children: tPackageDetails.rich('chargeable_weight_is'),
                      confirmLabel: tActions('got_it'),
                    });
                  }}
                />
              </Text>
              <Space flex={1} />
              <Text
                size="sm"
                c="secondary.5"
                fw={700}
                styles={{ root: { whiteSpace: 'nowrap' } }}
              >
                {formatNumber(
                  estBillableWeightData?.package_weight_details?.[0]
                    ?.billable_weight ?? 0
                )}{' '}
                (Kg)
              </Text>
            </Group>

            <Stack gap={rem(8)}>
              <Title order={3} size="h6" c="neutral.7" flex={1}>
                {tCommon('image')}
              </Title>

              <Controller
                control={control}
                name="package_detail.0.image_url"
                render={({ field: { onChange, value } }) => (
                  <UploadPhoto
                    images={value ?? []}
                    onChange={(value) => onChange(value)}
                    maxPhotoUpload={1}
                  />
                )}
              />
            </Stack>
          </Stack>
        </ScrollArea.Autosize>
      </Card>

      <Card
        radius={0}
        pos={{ base: 'fixed' }}
        w="100%"
        left={0}
        bottom={0}
        classNames={{
          root: 'z-30 sm:z-[1] !shadow-buttonDock',
        }}
      >
        <Stack gap="md">
          <SimpleGrid cols={2} spacing={rem(8)}>
            <Group
              gap={rem(8)}
              align="center"
              justify="center"
              pl="sm"
              pr="xl"
              wrap="nowrap"
            >
              <Weight className="size-5 flex-none" />
              <Text
                size="lg"
                fw={700}
                c={
                  estBillableWeightData?.total_billable_weight &&
                  estBillableWeightData?.total_billable_weight > 0
                    ? 'neutral.7'
                    : 'neutral.4'
                }
                lts={0.5}
                truncate
                title={formatNumber(
                  estBillableWeightData?.total_billable_weight ?? 0
                )}
              >
                {formatNumber(
                  estBillableWeightData?.total_billable_weight ?? 0
                )}
              </Text>
              <Text
                size="xs"
                fw={700}
                c={
                  estBillableWeightData?.total_billable_weight &&
                  estBillableWeightData?.total_billable_weight > 0
                    ? 'neutral.7'
                    : 'neutral.4'
                }
                lts={0.5}
                component="span"
                tt="uppercase"
                styles={{ root: { verticalAlign: 'middle' } }}
              >
                kg
              </Text>
            </Group>
            <Group
              gap={rem(8)}
              align="center"
              justify="space-between"
              ml={rem(-12)}
            >
              <Divider size="sm" orientation="vertical" mr={rem(4)} />
              <BoxBlue className="size-5 flex-none" />
              <Text
                size="lg"
                fw={700}
                c={
                  Object.values(totalLengthWidthHeight).some((v) => v > 0)
                    ? 'neutral.7'
                    : 'neutral.4'
                }
                lts={0.5}
                flex={1}
                truncate
                title={`${formatNumber(totalLengthWidthHeight.length)} x ${formatNumber(totalLengthWidthHeight.width)} x ${formatNumber(totalLengthWidthHeight.height)}`}
              >
                {`${formatNumber(totalLengthWidthHeight.length)} x ${formatNumber(totalLengthWidthHeight.width)} x ${formatNumber(totalLengthWidthHeight.height)} cm`}
              </Text>
            </Group>
          </SimpleGrid>

          <Button
            fullWidth
            size="md"
            h={rem(48)}
            disabled={
              invalidPackageDetails || !isValid || estimateError || isEstimating
            }
            type="submit"
          >
            {tActions('confirm')}
          </Button>
        </Stack>
      </Card>
    </form>
  );
}
